/**
 * Sticky add-to-cart bar. Shows/hides on scroll; two-way synced with the
 * main product form via the `variant:change` document event.
 */
(function () {
  'use strict';

  var selectors = {
    bar: '[data-sticky-add-to-cart-bar]',
    variantInput: '[data-sticky-variant-input]',
    addToCart: '[data-sticky-add-to-cart]',
    addToCartText: '[data-sticky-add-to-cart-text]',
    price: '[data-sticky-price]',
    variantLabel: '[data-sticky-variant-label]',
    thumbnail: '[data-sticky-thumbnail] img',
    sizeFieldset: 'fieldset[data-is-size="true"]',
    sizeToggler: '.size-conversion__toggler',
    sizeConversionLabel: '.js-toggle-size-conversion',
    sizeUnitLabel: '[data-sticky-size-unit]',
    quantityWrapper: '[data-sticky-quantity-wrapper]',
    quantityInput: '[data-sticky-quantity-input]',
    quantityMinus: '.js-qty__adjust--minus',
    quantityPlus: '.js-qty__adjust--plus',
    mainQuantityInput: 'input[name="quantity"]',
  };

  var classes = {
    visible: 'sticky-add-to-cart-bar--visible',
    disabled: 'disabled',
    selectSizeStep: 'select-size-step',
  };

  function StickyAddToCartBar(el) {
    this.el = el;
    this.formId = el.dataset.formId;
    this.form = this.formId ? document.getElementById(this.formId) : null;

    if (!this.form) {
      return;
    }

    this.mainProductSection = this.form.closest('[data-section-type="product"]');
    this.mainAddToCartBtn = this.mainProductSection
      ? this.mainProductSection.querySelector('[data-add-to-cart]')
      : null;

    // Bar hides again once the footer scrolls into view, so it never overlaps it.
    this.footer = document.querySelector('.site-footer');

    this.addToCartBtn = this.el.querySelector(selectors.addToCart);
    this.addToCartText = this.el.querySelector(selectors.addToCartText);
    this.priceEl = this.el.querySelector(selectors.price);
    this.variantLabelEl = this.el.querySelector(selectors.variantLabel);
    this.sizeUnitLabelEl = this.el.querySelector(selectors.sizeUnitLabel);
    this.thumbnailEl = this.el.querySelector(selectors.thumbnail);
    this.variantInputs = Array.prototype.slice.call(this.el.querySelectorAll(selectors.variantInput));
    this.optionNames = (el.dataset.optionNames || '').split('|').filter(Boolean);
    this.sizeOptionIndex =
      el.dataset.sizeOptionIndex !== '' && el.dataset.sizeOptionIndex != null
        ? parseInt(el.dataset.sizeOptionIndex, 10)
        : -1;

    this.namespace = '.sticky-add-to-cart-bar-' + (el.id || '');
    this.isVisible = false;

    this.bindEvents();
    this.syncFromMainForm();
    this.initializeButtonState();
    this.initSizeConversionSync();
    this.initQuantitySync();
  }

  StickyAddToCartBar.prototype.bindEvents = function () {
    if (this.mainAddToCartBtn) {
      var throttledScroll = theme.utils.throttle(20, this.handleScroll.bind(this));
      window.on('scroll' + this.namespace, throttledScroll);
      window.on('resize' + this.namespace, theme.utils.debounce(50, this.handleScroll.bind(this)));
      this.handleScroll();
    }

    // Keep the bound reference so destroy() can remove the document listener
    this.onVariantChange = this.handleVariantChange.bind(this);
    document.addEventListener('variant:change', this.onVariantChange);

    this.variantInputs.forEach(
      function (input) {
        input.addEventListener('change', this.handleBarInputChange.bind(this));
      }.bind(this)
    );
  };

  // Removes the listeners and observers that would outlive the section markup (window, document, main-form size
  // toggler). Listeners on elements of the section itself, such as the main quantity input, go away with its DOM.
  // Safe to call more than once; the bar element itself is removed by whoever swaps the markup.
  StickyAddToCartBar.prototype.destroy = function () {
    if (this.destroyed) {
      return;
    }
    this.destroyed = true;

    if (this.namespace) {
      window.off('scroll' + this.namespace);
      window.off('resize' + this.namespace);
    }

    if (this.onVariantChange) {
      document.removeEventListener('variant:change', this.onVariantChange);
      this.onVariantChange = null;
    }

    if (this.sizeObserver) {
      this.sizeObserver.disconnect();
      this.sizeObserver = null;
    }

    if (this.sizeToggler && this.onTogglerClick) {
      this.sizeToggler.removeEventListener('click', this.onTogglerClick);
      this.sizeToggler = null;
      this.onTogglerClick = null;
    }
  };

  StickyAddToCartBar.prototype.handleScroll = function () {
    requestAnimationFrame(this.updateVisibility.bind(this));
  };

  StickyAddToCartBar.prototype.updateVisibility = function () {
    if(!this.mainAddToCartBtn.closest('.product-block')) return;
    var rect = this.mainAddToCartBtn.closest('.product-block').getBoundingClientRect();
    var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    var mainButtonInView = rect.bottom > 0;

    var footerInView = false;
    if (this.footer) {
      footerInView = this.footer.getBoundingClientRect().top <= viewportHeight;
    }

    if (mainButtonInView || footerInView) {
      if (!this.isVisible) {
        return;
      }
      this.isVisible = false;

      // Move focus out before hiding (WCAG 2.4.3).
      if (this.el.contains(document.activeElement)) {
        document.activeElement.blur();
        document.body.focus();
      }

      this.el.classList.remove(classes.visible);
      this.el.setAttribute('aria-hidden', 'true');
    } else {
      if (this.isVisible) {
        return;
      }
      this.isVisible = true;
      this.el.classList.add(classes.visible);
      this.el.setAttribute('aria-hidden', 'false');
    }
  };

  // Catches up on the initial variant:change dispatch, which fires before this script attaches its listener.
  StickyAddToCartBar.prototype.syncFromMainForm = function () {
    if (!this.mainProductSection) {
      return;
    }

    var variantJsonEl = this.mainProductSection.querySelector('[data-variant-json]');
    var productSelectEl = this.mainProductSection.querySelector('[data-product-select]');

    if (!variantJsonEl || !productSelectEl || !productSelectEl.value) {
      return;
    }

    var variants;
    try {
      variants = JSON.parse(variantJsonEl.innerHTML);
    } catch (e) {
      return;
    }

    var currentId = productSelectEl.value;
    var variant = variants.filter(function (v) {
      return String(v.id) === String(currentId);
    })[0];

    if (variant) {
      this.handleVariantChange({ detail: { variant: variant } });
    }
  };

  // Runs last so the button matches the main form's real "select a size" state.
  StickyAddToCartBar.prototype.initializeButtonState = function () {
    if (!this.mainAddToCartBtn || !this.addToCartBtn || !this.addToCartText) {
      return;
    }

    var mainWrapper = this.mainAddToCartBtn.parentElement;

    if (!mainWrapper || !mainWrapper.classList.contains(classes.selectSizeStep)) {
      return;
    }

    var wrapper = this.addToCartBtn.parentElement;

    this.addToCartBtn.classList.add(classes.disabled);
    this.addToCartBtn.disabled = true;
    this.addToCartText.textContent = theme.strings.firstLoadButtonText;

    if (wrapper) {
      wrapper.classList.add(classes.selectSizeStep);
    }
  };

  // Mirrors the main form's size-conversion toggler onto the sticky bar's <select> (text only).
  StickyAddToCartBar.prototype.initSizeConversionSync = function () {
    if (!this.mainProductSection) {
      return;
    }

    var sizeFieldset = this.mainProductSection.querySelector(selectors.sizeFieldset);
    if (!sizeFieldset) {
      return;
    }

    var sizeSelect = this.el.querySelector(
      selectors.variantInput + '[data-index="' + sizeFieldset.dataset.index + '"]'
    );
    if (!sizeSelect) {
      return;
    }

    var toggler = this.mainProductSection.querySelector(selectors.sizeToggler);
    if (!toggler) {
      return;
    }

    this.sizeSelect = sizeSelect;

    this.sizeToggler = toggler;
    this.onTogglerClick = function (evt) {
      var label = evt.target.closest(selectors.sizeConversionLabel);
      if (label) {
        this.applySizeConversion(label.dataset.countryToggler);
      }
    }.bind(this);
    toggler.addEventListener('click', this.onTogglerClick);

    // theme.js can settle the active system without a click event, so watch class-attribute mutations too.
    var observer = (this.sizeObserver = new MutationObserver(
      function () {
        var active = toggler.querySelector(selectors.sizeConversionLabel + '.active');
        if (active) {
          this.applySizeConversion(active.dataset.countryToggler);
        }
      }.bind(this)
    ));

    observer.observe(toggler, { attributes: true, attributeFilter: ['class'], subtree: true });

    // In case the toggle already settled before this ran.
    var initialActive = toggler.querySelector(selectors.sizeConversionLabel + '.active');
    if (initialActive) {
      this.applySizeConversion(initialActive.dataset.countryToggler);
    }
  };

  // Hides options that have no `.product__size-item` under the active country system.
  StickyAddToCartBar.prototype.applySizeConversion = function (countryToggler) {
    if (!this.sizeSelect) {
      return;
    }

    if (this.sizeUnitLabelEl) {
      this.sizeUnitLabelEl.textContent = (countryToggler ? (countryToggler) : '');
    }

    Array.prototype.forEach.call(
      this.sizeSelect.options,
      function (option) {
        var value = option.value;
        var match = this.mainProductSection.querySelector(
          '.product__size-item[data-country="' +
            CSS.escape(countryToggler) +
            '"][data-regular-size="' +
            CSS.escape(value) +
            '"], .product__size-item[data-country="' +
            CSS.escape(countryToggler) +
            '"][data-value="' +
            CSS.escape(value) +
            '"]'
        );

        if (!match) {
          option.hidden = true;
          return;
        }

        option.hidden = false;

        var displayText = match.dataset.regularSize ? match.textContent.trim() : option.dataset.nativeText;
        option.dataset.displayText = displayText;
        option.textContent = option.disabled ? displayText + ' - ' + theme.strings.soldOut : displayText + ' - '+ countryToggler;
      }.bind(this)
    );

    // If the selected option just got hidden, fall back to the first visible one.
    var selectedIndex = this.sizeSelect.selectedIndex;
    var selected = selectedIndex !== -1 ? this.sizeSelect.options[selectedIndex] : null;

    if (selected && selected.hidden) {
      var firstVisible = null;

      for (var i = 0; i < this.sizeSelect.options.length; i++) {
        if (!this.sizeSelect.options[i].hidden) {
          firstVisible = this.sizeSelect.options[i];
          break;
        }
      }

      if (firstVisible) {
        this.sizeSelect.value = firstVisible.value;
        this.sizeSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // Refresh the label so a cold load shows the converted size immediately.
    this.updateVariantLabel(this.currentVariant);
  };

  StickyAddToCartBar.prototype.handleVariantChange = function (evt) {
    var variant = evt.detail && evt.detail.variant;

    this.currentVariant = variant;

    this.updateVariantInputs(variant);
    this.updatePrice(variant);
    this.updateVariantLabel(variant);
    this.updateThumbnail(variant);
    this.updateAddToCartButton(variant);
  };

  StickyAddToCartBar.prototype.updateVariantInputs = function (variant) {
    if (!variant) {
      return;
    }

    this.variantInputs.forEach(function (input) {
      var index = input.dataset.index;
      if (variant[index] !== undefined && input.value !== variant[index]) {
        input.value = variant[index];
      }
    });
  };

  StickyAddToCartBar.prototype.updatePrice = function (variant) {
    if (!variant || !this.priceEl || !window.theme.Currency) {
      return;
    }

    this.priceEl.innerHTML = theme.Currency.formatMoney(variant.price, theme.settings.moneyFormat);
  };

  // Uses the size <select>'s currently displayed text, reflecting the active conversion system.
  StickyAddToCartBar.prototype.updateVariantLabel = function (variant) {
    if (
      !variant ||
      !this.variantLabelEl ||
      !this.optionNames.length ||
      !variant.options ||
      this.sizeOptionIndex < 0 ||
      this.sizeOptionIndex >= variant.options.length
    ) {
      return;
    }

    var rawValue = variant.options[this.sizeOptionIndex];
    var displayValue = rawValue;

    if (this.sizeSelect) {
      var match = Array.prototype.filter.call(this.sizeSelect.options, function (option) {
        return option.value === rawValue;
      })[0];

      if (match) {
        displayValue = match.dataset.displayText || match.dataset.nativeText || rawValue;
      }
    }

    var label = this.optionNames[this.sizeOptionIndex] + ' ' + displayValue;

    this.variantLabelEl.textContent = ' · ' + label;
  };

  StickyAddToCartBar.prototype.updateThumbnail = function (variant) {
    if (!variant || !variant.featured_image || !this.thumbnailEl || !window.theme.Images) {
      return;
    }

    var src = variant.featured_image.src;
    this.thumbnailEl.src = theme.Images.getSizedImageUrl(src, '120x');
    this.thumbnailEl.srcset = '';
  };

  StickyAddToCartBar.prototype.updateAddToCartButton = function (variant) {
    if (!this.addToCartBtn || !this.addToCartText) {
      return;
    }

    if (variant) {
      if (variant.available) {
        this.addToCartBtn.classList.remove(classes.disabled);
        this.addToCartBtn.disabled = false;
        this.addToCartText.textContent = this.addToCartText.dataset.defaultText;
      } else {
        this.addToCartBtn.classList.add(classes.disabled);
        this.addToCartBtn.disabled = true;
        this.addToCartText.textContent = theme.strings.soldOut;
      }
    } else {
      this.addToCartBtn.classList.add(classes.disabled);
      this.addToCartBtn.disabled = true;
      this.addToCartText.textContent = theme.strings.unavailable;
    }

    var wrapper = this.addToCartBtn.parentElement;
    if (wrapper) {
      wrapper.classList.remove(classes.selectSizeStep);
    }
  };

  // Mirror the change onto the main form so it re-renders and broadcasts variant:change.
  StickyAddToCartBar.prototype.handleBarInputChange = function (evt) {
    if (!this.mainProductSection) {
      return;
    }

    var index = evt.target.dataset.index;
    var value = evt.target.value;
    var mainInputs = this.mainProductSection.querySelectorAll(
      '[data-variant-input][data-index="' + index + '"]'
    );

    mainInputs.forEach(function (input) {
      var type = input.getAttribute('type');

      if (type === 'radio' || type === 'checkbox') {
        if (input.value === value) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else {
        input.value = value;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  };

  // UI-only mirror of the main form's quantity input (theme.QtySelector doesn't dispatch `change`).
  StickyAddToCartBar.prototype.initQuantitySync = function () {
    if (!this.mainProductSection) {
      return;
    }

    this.quantityWrapper = this.el.querySelector(selectors.quantityWrapper);
    this.quantityInput = this.el.querySelector(selectors.quantityInput);
    this.mainQuantityInput = this.mainProductSection.querySelector(selectors.mainQuantityInput);

    if (!this.quantityWrapper || !this.quantityInput || !this.mainQuantityInput) {
      return;
    }

    // Initial sync from whatever quantity the main form already rendered.
    this.quantityInput.value = this.mainQuantityInput.value || this.quantityInput.value;

    var minusBtn = this.quantityWrapper.querySelector(selectors.quantityMinus);
    var plusBtn = this.quantityWrapper.querySelector(selectors.quantityPlus);

    if (minusBtn) {
      minusBtn.addEventListener(
        'click',
        function () {
          this.changeQuantity(this.getQuantity() - 1);
        }.bind(this)
      );
    }

    if (plusBtn) {
      plusBtn.addEventListener(
        'click',
        function () {
          this.changeQuantity(this.getQuantity() + 1);
        }.bind(this)
      );
    }

    this.quantityInput.addEventListener(
      'change',
      function () {
        this.changeQuantity(this.getQuantity());
      }.bind(this)
    );

    // Mirror back any change made to the main form's own quantity input.
    this.mainQuantityInput.addEventListener(
      'change',
      function () {
        if (this.quantityInput.value !== this.mainQuantityInput.value) {
          this.quantityInput.value = this.mainQuantityInput.value;
        }
      }.bind(this)
    );
  };

  StickyAddToCartBar.prototype.getQuantity = function () {
    var value = parseInt(this.quantityInput.value, 10);
    return isNaN(value) ? 1 : value;
  };

  StickyAddToCartBar.prototype.changeQuantity = function (qty) {
    var min = parseInt(this.quantityInput.getAttribute('min'), 10) || 1;

    if (qty < min) {
      qty = min;
    }

    this.quantityInput.value = qty;

    if (this.mainQuantityInput.value !== String(qty)) {
      this.mainQuantityInput.value = qty;
      this.mainQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  function init() {
    document.querySelectorAll(selectors.bar).forEach(function (el) {
      if (el.stickyAddToCartBarInstance) {
        return;
      }
      el.stickyAddToCartBarInstance = new StickyAddToCartBar(el);
    });
  }

  // Destroys every bar instance in the document (or only those inside `scope`) so a swapped-out product leaves no listeners behind
  function destroy(scope) {
    (scope || document).querySelectorAll(selectors.bar).forEach(function (el) {
      if (el.stickyAddToCartBarInstance) {
        el.stickyAddToCartBarInstance.destroy();
        el.stickyAddToCartBarInstance = null;
      }
    });
  }

  // Public API for assets/product-color-switch.js (re-init after a section swap)
  window.theme = window.theme || {};
  window.theme.stickyAddToCartBar = { init: init, destroy: destroy };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();